import { Link } from "react-router-dom";
import {
  Receipt, Camera, Coins, PieChart, Scale, Users, Sparkles, UserPlus,
  ArrowRight, Check, Wallet,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { AppTopBar } from "../components/AppScreen";

/* Trip Expense Manager — product page for the trip-money pillar (Feb-2026 unbundling).
   Every capability listed is live in per-trip expense tracking. */

const FEATURES = [
  { icon: Wallet, color: "text-emerald-700", title: "Budget vs actual",
    text: "Set a total trip budget and per-category budgets — watch actual spending track against the plan with live progress bars." },
  { icon: Camera, color: "text-blue-700", title: "Receipt capture with AI",
    text: "Photograph a receipt and AI reads the merchant, amount and category for you. Every receipt browsable in the trip gallery." },
  { icon: Coins, color: "text-amber-600", title: "Multi-currency, converted",
    text: "Log expenses in any currency — everything converts to your home currency with daily ECB rates." },
  { icon: Users, color: "text-violet-700", title: "The whole family logs",
    text: "Everyone on a shared trip adds what they paid under their own name — with 'added by' attribution on every entry." },
  { icon: Scale, color: "text-rose-600", title: "Who-owes-whom settlement",
    text: "One tap shows every member's balance and the minimal set of transfers to settle up — shareable to WhatsApp." },
  { icon: PieChart, color: "text-cyan-700", title: "Where the money goes",
    text: "Category breakdowns, per-day pacing and AI spending insights that spot savings opportunities from your patterns." },
];

export default function TripExpenseManagerPage() {
  const { user } = useAuth();
  return (
    <div data-testid="trip-expense-page">
      <AppTopBar title="Expenses" />
      <section className="relative border-b border-zinc-200 bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-300 mb-3 flex items-center gap-2"><Receipt className="w-4 h-4" /> Trip Expense Manager — by TravelerPulse</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl" data-testid="trip-expense-title">
            Every trip cost, captured.<br />Every debt, settled.
          </h1>
          <p className="text-white/85 text-lg mt-4 max-w-2xl">
            Budgets, receipts, currencies and splits for group trips — so the money conversation
            takes five minutes at the end, not five arguments during.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to={user ? "/travel-hub#hub-expenses" : "/register"} data-testid="trip-expense-cta"
              className="inline-flex items-center gap-2 bg-emerald-500 text-white px-7 py-3.5 font-700 hover:bg-emerald-400 transition-colors">
              <UserPlus className="w-5 h-5" /> {user ? "Open my Expense Tracker" : "Start free — 45 days, no card"}
            </Link>
            <Link to="/family-expense-hub" data-testid="trip-expense-family-link"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white px-7 py-3.5 font-700 hover:bg-white/20 transition-colors">
              Household spending instead? <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="trip-expense-features">
        <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-8">Built for real trips with real people</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="border border-zinc-300 bg-white p-5">
              <f.icon className={`w-7 h-7 ${f.color}`} />
              <div className="font-heading font-900 text-lg mt-3">{f.title}</div>
              <p className="text-sm text-muted-foreground mt-1.5">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14" data-testid="trip-expense-how">
          <h2 className="font-heading font-900 text-2xl mb-5">From receipt to settlement</h2>
          <ol className="space-y-3 text-sm text-zinc-700">
            {[
              "Set the trip budget — total and per category (food, stays, transport…).",
              "Everyone logs as they spend: snap the receipt, AI fills in the details, any currency welcome.",
              "Watch the dashboard: budget bars, category donut and per-person totals update live.",
              "At the end, open Settle Up — minimal transfers, who pays whom, share it to the family WhatsApp.",
            ].map((s, i) => (
              <li key={i} className="flex gap-3"><span className="shrink-0 w-6 h-6 bg-emerald-700 text-white text-xs font-800 flex items-center justify-center">{i + 1}</span>{s}</li>
            ))}
          </ol>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={user ? "/travel-hub#hub-expenses" : "/register"} data-testid="trip-expense-bottom-cta"
              className="inline-flex items-center gap-2 bg-emerald-700 text-white px-6 py-3 font-700 hover:bg-emerald-600 transition-colors">
              {user ? "Track my trip spending" : "Create your free account"} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/plan-ai" className="inline-flex items-center gap-2 border border-zinc-300 px-6 py-3 font-700 text-zinc-700 hover:border-primary hover:text-primary transition-colors">
              <Sparkles className="w-4 h-4" /> Plan the trip first with AI
            </Link>
          </div>
          <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Expense data is private by default — share it only with people you name as financial managers.</p>
        </div>
      </section>
    </div>
  );
}
